import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";

/* ---------- Reveal-on-scroll wrapper ---------- */
export function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("in-view");
          obs.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const delayClass = delay ? ` gee-reveal-delay-${delay}` : "";
  return (
    <div ref={ref} className={`gee-reveal${delayClass} ${className}`}>
      {children}
    </div>
  );
}

/* ---------- Animated counter ---------- */
export function Counter({ value, suffix = "", duration = 1800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(eased * value));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.unobserve(el);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className="gee-stat-value">
      {display}{suffix}
    </span>
  );
}

/* ---------- Page hero (inner pages) ---------- */
export function PageHero({ image, kicker, title, children }) {
  return (
    <section className="gee-hero pt-24" style={{ minHeight: "52vh" }}>
      <div className="gee-hero-bg" style={{ backgroundImage: `url(${image})` }} />
      <div className="gee-hero-overlay" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-28">
        {kicker && <p className="gee-kicker mb-4">{kicker}</p>}
        <h1 className="font-serif-d text-white text-4xl md:text-6xl leading-tight max-w-3xl">
          {title}
        </h1>
        {children}
      </div>
      <div className="gee-hero-notch" />
    </section>
  );
}

/* ---------- News card ---------- */
export function NewsCard({ post, delay = 0 }) {
  const dateStr = new Date(post.date).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
  return (
    <Reveal delay={delay}>
      <article className="gee-card h-full flex flex-col" data-testid={`news-card-${post.slug}`}>
        <Link to={`/blog/${post.slug}`} className="gee-img-frame block aspect-[4/3]">
          <img src={post.image} alt={post.title} loading="lazy" />
        </Link>
        <div className="p-7 flex flex-col flex-1">
          <div className="flex items-center gap-4 mb-3 text-xs">
            <span className="bg-[#eaf5fc] text-[#1d8fc0] px-3 py-1 font-medium uppercase tracking-wider">
              {post.category}
            </span>
            <span className="flex items-center gap-1.5 text-[#8aa0b0]">
              <Calendar size={13} /> {dateStr}
            </span>
          </div>
          <h3 className="font-serif-d text-xl md:text-[1.35rem] text-[#0f2438] leading-snug mb-3">
            <Link to={`/blog/${post.slug}`} className="hover:text-[#29abe2] transition-colors">
              {post.title}
            </Link>
          </h3>
          <p className="text-[#5b6d7d] text-sm leading-relaxed mb-5 flex-1">{post.excerpt}</p>
          <Link
            to={`/blog/${post.slug}`}
            aria-label={`Read more about ${post.title}`}
            className="gee-link text-[#0f2438] text-sm w-fit"
          >
            Read More <ArrowRight size={15} className="gee-link-arrow" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}
